import React, { useState, useEffect, useRef } from 'react';
import { useEditMode } from '../contexts/EditModeContext';
import { useLocation, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Hero from '../components/Hero/Hero';
import Countdown from '../components/Countdown';
import YouAreInvited from '../components/YouAreInvited';
import Details from '../components/Details';
import Schedule from '../components/Schedule';
import Location from '../components/Location';
import RSVP from '../components/RSVP';
import GenericSection from '../components/GenericSection';
import SectionManager from '../components/SectionManager';
import Toast from '../components/Toast';
import EntryLetter from '../components/EntryLetter';
import { useLanguage } from '../contexts/LanguageContext';

function Home({ content, setContent }) {
  const { isEditMode } = useEditMode();
  const location = useLocation();
  const { lang } = useLanguage();
  const [editedContent, setEditedContent] = useState(content);
  const [sections, setSections] = useState([]);
  const [editedSections, setEditedSections] = useState([]);
  const [toast, setToast] = useState(null);
  const [viewingDraft, setViewingDraft] = useState(true);
  const [staleDraft, setStaleDraft] = useState(null);
  const autoSaveTimeout = useRef(null);
  const [invitationOpened, setInvitationOpened] = useState(() => {
    return sessionStorage.getItem('invitation_opened') === 'true';
  });

  // Set theme class on body to manage global background states
  useEffect(() => {
    document.body.classList.add('theme-v2-active');
    return () => {
      document.body.classList.remove('theme-v2-active');
    };
  }, []);

  // Fetch sections
  useEffect(() => {
    fetch(`/api/sections?lang=${lang}`)
      .then(res => res.json())
      .then(data => {
        setSections(data.sections || []);
        setEditedSections(data.sections || []);
      })
      .catch(err => console.error('Failed to load sections:', err));
  }, [lang]);

  // Scroll to hash on mount or location change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Restore draft on mount if in edit mode and it's based on current content
  useEffect(() => {
    const draftData = localStorage.getItem(`editedContentDraft_${lang}`);
    if (draftData && isEditMode) {
      try {
        const { draft, baseHash } = JSON.parse(draftData);
        const currentHash = JSON.stringify(content);

        if (JSON.stringify(draft) !== currentHash) {
          // Draft has changes
          setEditedContent(draft);
          setViewingDraft(true);

          if (baseHash === currentHash) {
            // Draft is current
            setToast({ message: 'Draft restored', type: 'success' });
            setStaleDraft(null);
          } else {
            // Draft is stale - save it and warn user
            setStaleDraft(draft);
            setToast({
              message: 'Warning: Content was updated by another user. Use the toggle button to switch between your draft and latest content.',
              type: 'error',
            });
          }
        } else {
          setStaleDraft(null);
        }
      } catch (e) {
        console.error('Failed to restore draft');
        localStorage.removeItem(`editedContentDraft_${lang}`);
      }
    }
  }, [isEditMode, content, lang]);

  // Reset edited content when original content changes
  useEffect(() => {
    if (!isEditMode) {
      setEditedContent(content);
    }
  }, [content, isEditMode]);

  // Auto-save to localStorage with debounce
  useEffect(() => {
    if (isEditMode) {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
      autoSaveTimeout.current = setTimeout(() => {
        const draftData = {
          draft: editedContent,
          baseHash: JSON.stringify(content),
        };
        localStorage.setItem(`editedContentDraft_${lang}`, JSON.stringify(draftData));
      }, 500);
    }
    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [editedContent, isEditMode, content, lang]);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('editToken');

      // Save content
      const contentRes = await fetch(`/api/content?lang=${lang}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editedContent),
      });

      // Save sections
      const sectionsRes = await fetch(`/api/sections?lang=${lang}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sections: editedSections }),
      });

      const contentData = await contentRes.json();
      const sectionsData = await sectionsRes.json();

      if (contentData.success && sectionsData.success) {
        setContent(editedContent);
        setSections(editedSections);
        localStorage.removeItem(`editedContentDraft_${lang}`);
        setStaleDraft(null);
        setToast({ message: 'Changes saved successfully!', type: 'success' });
      } else {
        if (contentRes.status === 401 || sectionsRes.status === 401) {
          localStorage.removeItem('editToken');
          window.location.reload();
        }
        setToast({ message: 'Failed to save: ' + (contentData.error || sectionsData.error || 'Unknown error'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Failed to save changes', type: 'error' });
    }
  };

  const handleCancel = () => {
    setEditedContent(content);
    setEditedSections(sections);
    localStorage.removeItem(`editedContentDraft_${lang}`);
    setStaleDraft(null);
    setViewingDraft(true);
  };

  const migrateToSections = () => {
    const sections = [
      { id: 'story', type: 'story', title: 'Our Story' },
      { id: 'details', type: 'details', title: 'Details' },
      { id: 'schedule', type: 'schedule', title: 'Schedule' },
      { id: 'location', type: 'location', title: 'Location' },
      { id: 'rsvp', type: 'rsvp', title: 'RSVP' },
    ];
    setEditedContent({ ...editedContent, sections });
    setToast({ message: 'Sections array created! Click Save to apply.', type: 'success' });
  };

  if (!content) return null;

  const displayContent = isEditMode && !viewingDraft ? content : editedContent;

  const toggleView = () => {
    if (viewingDraft) {
      setEditedContent(content);
      setViewingDraft(false);
    } else {
      setEditedContent(staleDraft || editedContent);
      setViewingDraft(true);
    }
  };

  return (
    <>
      {!invitationOpened && (
        <EntryLetter
          onOpen={() => {
            setInvitationOpened(true);
            sessionStorage.setItem('invitation_opened', 'true');
          }}
        />
      )}
      {invitationOpened && (
        <div className="theme-v2" style={{ overflow: 'hidden' }}>
          <div className="main-content" style={{ position: 'relative' }}>
            {isEditMode && (
              <div className="edit-bar">
                <button className="btn" onClick={handleSave}>
                  Save Changes
                </button>
                <button className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                {staleDraft && (
                  <button className="btn btn-secondary" onClick={toggleView}>
                    {viewingDraft ? 'View Latest' : 'View Draft'}
                  </button>
                )}
                <Link to="/admin/guests" className="btn btn-secondary">
                  Manage Guests
                </Link>
              </div>
            )}
            <SectionManager sections={isEditMode ? editedSections : sections} onChange={setEditedSections} />
            <Navigation />
            <Hero hero={displayContent.hero} setHero={hero => setEditedContent({ ...editedContent, hero })} />

            {(isEditMode ? editedSections : sections).length > 0 ? (
              (isEditMode ? editedSections : sections).map((section, index) => {
                const sectionProps = {
                  section,
                  onChange: updated => {
                    const newSections = [...editedSections];
                    newSections[index] = updated;
                    setEditedSections(newSections);
                  },
                };

                switch (section.type) {
                  case 'invitation':
                    return (
                      <div key={section.id} style={{ position: 'relative' }}>
                        <YouAreInvited
                          invitation={displayContent.invitation}
                          setInvitation={invitation => setEditedContent({ ...editedContent, invitation })}
                        />
                      </div>
                    );
                  case 'countdown':
                    return (
                      <div key={section.id} style={{ position: 'relative' }}>
                        <Countdown
                          targetDate="2027-08-31T17:00:00"
                          weddingDate={displayContent.weddingDate || '31st August 2026'}
                          setWeddingDate={weddingDate => setEditedContent({ ...editedContent, weddingDate })}
                        />
                      </div>
                    );
                  case 'details':
                    return (
                      <div key={section.id} style={{ position: 'relative' }}>
                        <img src="/flowers/IMG-20251023-WA0006.png" alt="" className="section-flower section-flower-right" loading="lazy" />
                        <Details
                          details={displayContent.details}
                          setDetails={details => setEditedContent({ ...editedContent, details })}
                          subtitle={displayContent.detailsSubtitle}
                          setSubtitle={detailsSubtitle => setEditedContent({ ...editedContent, detailsSubtitle })}
                        />
                      </div>
                    );
                  case 'schedule':
                    return (
                      <div key={section.id} style={{ position: 'relative' }}>
                        <img src="/flowers/IMG-20251023-WA0007.png" alt="" className="section-flower section-flower-left" loading="lazy" />
                        <Schedule schedule={displayContent.schedule} setSchedule={schedule => setEditedContent({ ...editedContent, schedule })} />
                      </div>
                    );
                  case 'location':
                    return (
                      <div key={section.id} style={{ position: 'relative' }}>
                        <img src="/flowers/IMG-20251023-WA0009.png" alt="" className="section-flower section-flower-right" loading="lazy" />
                        <Location location={displayContent.location} setLocation={location => setEditedContent({ ...editedContent, location })} />
                      </div>
                    );
                  case 'rsvp':
                    return <RSVP key={section.id} content={displayContent} onContentChange={isEditMode ? setEditedContent : null} />;
                  case 'generic':
                    return <GenericSection key={section.id} {...sectionProps} />;
                  default:
                    return null;
                }
              })
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <img src="/flowers/IMG-20251023-WA0006.png" alt="" className="section-flower section-flower-right" loading="lazy" />
                  <Details
                    details={displayContent.details}
                    setDetails={details => setEditedContent({ ...editedContent, details })}
                    subtitle={displayContent.detailsSubtitle}
                    setSubtitle={detailsSubtitle => setEditedContent({ ...editedContent, detailsSubtitle })}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <img src="/flowers/IMG-20251023-WA0007.png" alt="" className="section-flower section-flower-left" loading="lazy" />
                  <Schedule schedule={displayContent.schedule} setSchedule={schedule => setEditedContent({ ...editedContent, schedule })} />
                </div>

                <div style={{ position: 'relative' }}>
                  <img src="/flowers/IMG-20251023-WA0009.png" alt="" className="section-flower section-flower-right" loading="lazy" />
                  <Location location={displayContent.location} setLocation={location => setEditedContent({ ...editedContent, location })} />
                </div>

                <RSVP content={displayContent} onContentChange={isEditMode ? setEditedContent : null} />
              </>
            )}

            <div className="flower-background">
              {[...Array(5)].map((_, i) => (
                <img key={i} src="/flowers/IMG-20251023-WA0012.png" alt="" className="flower-tile" loading="lazy" />
              ))}
            </div>
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
          </div>
        </div>
      )}
    </>
  );
}

export default Home;
